import { type ReactElement, useEffect, useMemo, useState } from 'react';
import type { GovernedSettingBucketView } from '@vello/api-client';
import type { VeloIntentView } from '@vello/api-client';
import { AI_PURPOSES, CENTRAL_ASSISTANT_LANGUAGES, type AiPurpose, type AutonomyLevel } from '@vello/config';
import { Button, CapWidth, Checkbox, Cluster, Field, Grid, MoneyInput, Notice, Select, Stack, Switch, Text, TextArea, TextInput } from '@vello/web-theme';
import {
  ORDER_NON_RIDE_SERVICES,
  addOrderService,
  aiBudgetDraftOf,
  aiBudgetValue,
  aiModelRowsOf,
  aiModelsValue,
  assistantDraftOf,
  assistantToggleLanguage,
  assistantWithDisclosure,
  assistantWithName,
  autonomyDraftOf,
  autonomyLevelChoices,
  autonomyToggleDisabled,
  autonomyValue,
  autonomyWithDefaultLevel,
  autonomyWithEnabled,
  autonomyWithIntentLevel,
  countryPackCurrencyAt,
  orderPaymentsValue,
  orderServicesOf,
  parseAlertPercents,
  refundCapsDraftOf,
  refundCapsInverted,
  refundCapsValue,
  removeOrderService,
  rideCategoryOfService,
  settingEditorFor,
  speechCredentialRefOf,
  speechEngineDraftOf,
  speechEngineValue,
  toggleServiceMethod,
  withAiModelRow,
  type AiBudgetDraft,
  type AiModelRow,
  type AssistantDraft,
  type AutonomyDraft,
  type OrderPaymentServices,
  type RefundCapsDraft,
  type SpeechEngineDraft,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * The typed editors of Admin › Atendimento e IA (D631, doc 25 section 11) - one FORM per governed key, never raw JSON. Each is a
 * controlled-by-remount control: the screen gives it a `key` that changes with the key, the bucket and the version in force, so its
 * local draft restarts from what governs. It reports the value to propose through `onValue`, or `undefined` while the draft is
 * incomplete (the screen keeps the primary action off); it never validates what the server owns - the preview and the propose do.
 */
export interface CentralAiEditorProps {
  readonly kind: ReturnType<typeof settingEditorFor>['kind'];
  readonly value: unknown;
  readonly bucket: GovernedSettingBucketView;
  readonly now: string;
  readonly onValue: (value: unknown) => void;
}

export function CentralAiValueEditor({ kind, value, bucket, now, onValue }: CentralAiEditorProps): ReactElement | null {
  switch (kind) {
    case 'assistant':
      return <AssistantEditor value={value} onValue={onValue} />;
    case 'refundCaps':
      return <RefundCapsEditor value={value} bucket={bucket} now={now} onValue={onValue} />;
    case 'orderPayments':
      return <OrderPaymentsEditor value={value} bucket={bucket} now={now} onValue={onValue} />;
    case 'aiModels':
      return <AiModelsEditor value={value} bucket={bucket} onValue={onValue} />;
    case 'speechEngine':
      return <SpeechEngineEditor value={value} onValue={onValue} />;
    case 'aiBudget':
      return <AiBudgetEditor value={value} bucket={bucket} now={now} onValue={onValue} />;
    case 'autonomy':
      return <AutonomyEditor value={value} onValue={onValue} />;
    default:
      return null;
  }
}

// ---- central.assistant ---------------------------------------------------------------------------

function AssistantEditor({ value, onValue }: { value: unknown; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const [draft, setDraft] = useState<AssistantDraft>(() => assistantDraftOf(value));
  const emit = (next: AssistantDraft): void => {
    setDraft(next);
    const complete = next.displayName.trim() !== '' && next.languages.length > 0 && next.languages.every((l) => (next.disclosure[l] ?? '').trim() !== '');
    onValue(
      complete
        ? {
            displayName: next.displayName.trim(),
            languages: [...next.languages],
            disclosure: Object.fromEntries(next.languages.map((l) => [l, (next.disclosure[l] ?? '').trim()])),
          }
        : undefined,
    );
  };
  return (
    <Stack gap={4}>
      <CapWidth size="sm">
        <Field label={t('centralai.assistant.name')} htmlFor="centralai-name" hint={t('centralai.assistant.name.hint')}>
          <TextInput id="centralai-name" autoComplete="off" maxLength={40} value={draft.displayName} onChange={(e) => emit(assistantWithName(draft, e.target.value))} />
        </Field>
      </CapWidth>
      <div>
        <Text role="caption" tone="secondary" block>{t('centralai.assistant.languages')}</Text>
        <Cluster gap={2} wrap>
          {CENTRAL_ASSISTANT_LANGUAGES.map((l) => (
            <button key={l} type="button" className="vello-chip" aria-pressed={draft.languages.includes(l)} onClick={() => emit(assistantToggleLanguage(draft, l))}>
              {v('central.assistant_language', l)}
            </button>
          ))}
        </Cluster>
        <Text as="p" role="caption" tone="tertiary">{t('centralai.assistant.languages.hint')}</Text>
      </div>
      {draft.languages.map((l) => (
        <Field key={l} label={t('centralai.assistant.disclosure', { language: v('central.assistant_language', l) })} htmlFor={`centralai-disclosure-${l}`} hint={t('centralai.assistant.disclosure.hint')}>
          <TextArea id={`centralai-disclosure-${l}`} rows={3} maxLength={500} value={draft.disclosure[l] ?? ''} onChange={(e) => emit(assistantWithDisclosure(draft, l, e.target.value))} />
        </Field>
      ))}
    </Stack>
  );
}

// ---- money keys: the currency ---------------------------------------------------------------------

/**
 * The currency a money value of this bucket is typed in: the one the draft or the version in force carries, else the country pack's
 * (the APPROVED pack in force). `undefined` while it cannot be told - the editor then asks for it, never guesses one.
 */
function useBucketCurrency(known: readonly unknown[], bucket: GovernedSettingBucketView, now: string): { readonly currency: string | undefined; readonly looking: boolean } {
  const { runtime } = useAdmin();
  const fromValues = known.find((c): c is string => typeof c === 'string' && /^[A-Z]{3}$/.test(c));
  const country = bucket.scope === 'country' ? bucket.scopeRef : '';
  const [pack, setPack] = useState<{ country: string; currency: string | undefined } | undefined>(undefined);
  useEffect(() => {
    if (fromValues !== undefined || country === '') return undefined;
    let alive = true;
    void (async () => {
      const res = await runtime.config.listCountryPacks(country);
      if (alive) setPack({ country, currency: res.ok ? countryPackCurrencyAt(res.value.packs, now) : undefined });
    })();
    return () => {
      alive = false;
    };
  }, [runtime, fromValues, country, now]);
  const fromPack = pack !== undefined && pack.country === country ? pack.currency : undefined;
  return { currency: fromValues ?? fromPack, looking: fromValues === undefined && country !== '' && (pack === undefined || pack.country !== country) };
}

/** Asks for the ISO 4217 code when no currency can be told (a platform total, or a country without an approved pack). */
function CurrencyField({ value, onChange }: { value: string; onChange: (currency: string) => void }): ReactElement {
  const { t } = useT();
  return (
    <CapWidth size="xs">
      <Field label={t('centralai.currency')} htmlFor="centralai-currency" hint={t('centralai.currency.hint')}>
        <TextInput id="centralai-currency" autoComplete="off" maxLength={3} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      </Field>
    </CapWidth>
  );
}

function Amount({ id, label, hint, currency, valueMinor, onChange }: { id: string; label: string; hint: string; currency: string; valueMinor: bigint | undefined; onChange: (minor: bigint | undefined) => void }): ReactElement {
  const { lang } = useT();
  const { v } = useVocab();
  return (
    <CapWidth size="sm">
      <MoneyInput
        id={id}
        label={label}
        currency={currency}
        locale={lang}
        valueMinor={valueMinor}
        onChangeMinor={onChange}
        issueText={(issue) => v('format.money_input_issue', issue)}
        hint={hint}
        maxMinor={BigInt(Number.MAX_SAFE_INTEGER)}
        required
      />
    </CapWidth>
  );
}

// ---- central.autoRefundCaps ----------------------------------------------------------------------

function RefundCapsEditor({ value, bucket, now, onValue }: { value: unknown; bucket: GovernedSettingBucketView; now: string; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const stored = (value as { currency?: unknown } | null | undefined)?.currency;
  const { currency: known, looking } = useBucketCurrency([stored, (bucket.current?.value as { currency?: unknown } | undefined)?.currency], bucket, now);
  const [typed, setTyped] = useState('');
  const currency = known ?? typed;
  const [draft, setDraft] = useState<RefundCapsDraft>(() => refundCapsDraftOf(value, ''));
  const emit = (next: RefundCapsDraft): void => {
    setDraft(next);
    onValue(refundCapsValue({ ...next, currency }));
  };
  // The currency can arrive after the first render (the country pack): re-emit so the value carries it.
  useEffect(() => {
    onValue(refundCapsValue({ ...draft, currency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);
  if (looking) return <Notice tone="neutral">{t('govset.editor.money.loading')}</Notice>;
  return (
    <Stack gap={4}>
      <Notice tone="neutral">{t('centralai.caps.explain')}</Notice>
      {known === undefined && <CurrencyField value={typed} onChange={setTyped} />}
      {/^[A-Z]{3}$/.test(currency) ? (
        <>
          <Amount id="centralai-cap-order" label={t('centralai.caps.order')} hint={t('centralai.caps.order.hint', { currency })} currency={currency} valueMinor={draft.perOrderMinor} onChange={(m) => emit({ ...draft, perOrderMinor: m })} />
          <Amount id="centralai-cap-month" label={t('centralai.caps.month')} hint={t('centralai.caps.month.hint', { currency })} currency={currency} valueMinor={draft.perCustomerMonthMinor} onChange={(m) => emit({ ...draft, perCustomerMonthMinor: m })} />
          {refundCapsInverted(draft) && <Notice tone="warning">{t('centralai.caps.inverted')}</Notice>}
        </>
      ) : (
        <Notice tone="warning">{t('govset.editor.money.noCurrency')}</Notice>
      )}
    </Stack>
  );
}

// ---- central.orderPaymentMethods -----------------------------------------------------------------

/** The ride categories the country's markets offer (best effort: a market whose catalog cannot be read adds none). */
function useCountryRideCategories(country: string): readonly { readonly code: string; readonly label: string }[] {
  const { runtime, markets } = useAdmin();
  const { lang } = useT();
  const [found, setFound] = useState<readonly { code: string; label: string }[]>([]);
  const ids = useMemo(() => markets.filter((m) => m.countryCode.toUpperCase() === country).map((m) => m.id).slice(0, 6), [markets, country]);
  useEffect(() => {
    if (ids.length === 0) return undefined;
    let alive = true;
    void (async () => {
      const byCode = new Map<string, string>();
      for (const id of ids) {
        const res = await runtime.serviceCategories.market(id);
        if (!res.ok) continue;
        for (const c of res.value.categories) if (c.kind === 'mobility' && c.status === 'ACTIVE') byCode.set(c.code, c.labels[lang] ?? c.labels.pt ?? c.labels.en ?? '');
      }
      if (alive) setFound([...byCode.entries()].map(([code, label]) => ({ code, label })).sort((a, b) => a.code.localeCompare(b.code)));
    })();
    return () => {
      alive = false;
    };
  }, [runtime, ids, lang]);
  return found;
}

/** The payment methods the country has registered (`payment.capabilities`), or `undefined` while loading / when none. */
function useRegisteredMethods(country: string, now: string): { readonly methods: readonly string[] | undefined; readonly loaded: boolean } {
  const { runtime } = useAdmin();
  const [state, setState] = useState<{ country: string; methods: readonly string[] | undefined } | undefined>(undefined);
  useEffect(() => {
    if (country === '') return undefined;
    let alive = true;
    void (async () => {
      const res = await runtime.config.resolvePolicy('payment.capabilities', { country }, now);
      const raw = res.ok ? res.value.resolution.value : undefined;
      if (alive) setState({ country, methods: Array.isArray(raw) ? raw.filter((m): m is string => typeof m === 'string') : undefined });
    })();
    return () => {
      alive = false;
    };
  }, [runtime, country, now]);
  const mine = state !== undefined && state.country === country ? state : undefined;
  return { methods: mine?.methods, loaded: mine !== undefined };
}

function OrderPaymentsEditor({ value, bucket, now, onValue }: { value: unknown; bucket: GovernedSettingBucketView; now: string; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const country = bucket.scope === 'country' ? bucket.scopeRef : '';
  const [services, setServices] = useState<OrderPaymentServices>(() => orderServicesOf(value));
  const [adding, setAdding] = useState('');
  const { methods, loaded } = useRegisteredMethods(country, now);
  const categories = useCountryRideCategories(country);
  const emit = (next: OrderPaymentServices): void => {
    setServices(next);
    onValue(Object.keys(next).length === 0 ? undefined : orderPaymentsValue(next));
  };
  const labelOf = (service: string): string => {
    const category = rideCategoryOfService(service);
    if (category === undefined) return v('central.order_service', service);
    const named = categories.find((c) => c.code === category)?.label;
    return t('centralai.pay.ride', { category: named !== undefined && named !== '' ? named : v('ride.category', category) });
  };
  const offered = [
    ...ORDER_NON_RIDE_SERVICES.map((s) => ({ service: s as string, label: v('central.order_service', s) })),
    ...categories.map((c) => ({ service: `ride:${c.code}`, label: t('centralai.pay.ride', { category: c.label !== '' ? c.label : v('ride.category', c.code) }) })),
  ].filter((o) => !(o.service in services));
  const registered = methods ?? [];
  const stale = Object.values(services).flat().filter((m) => loaded && !registered.includes(m));
  return (
    <Stack gap={4}>
      <Notice tone="neutral">{t('centralai.pay.explain')}</Notice>
      {loaded && methods === undefined && <Notice tone="warning">{t('centralai.pay.noFrame')}</Notice>}
      {stale.length > 0 && <Notice tone="warning">{t('centralai.pay.unregistered')}</Notice>}
      {Object.keys(services).length === 0 && <Notice tone="neutral">{t('centralai.pay.empty')}</Notice>}
      {Object.keys(services).sort().map((service) => (
        <div key={service}>
          <Cluster gap={3} align="baseline" wrap>
            <Text role="label">{labelOf(service)}</Text>
            <Button size="sm" variant="secondary" onClick={() => emit(removeOrderService(services, service))}>{t('centralai.pay.remove')}</Button>
          </Cluster>
          <Cluster gap={2} wrap>
            {[...new Set([...registered, ...(services[service] ?? [])])].map((m) => (
              <button key={m} type="button" className="vello-chip" aria-pressed={(services[service] ?? []).includes(m)} onClick={() => emit(toggleServiceMethod(services, service, m))}>
                {v('central.payment_method_token', m)}
              </button>
            ))}
          </Cluster>
          {(services[service] ?? []).length === 0 && <Text as="p" role="caption" tone="tertiary">{t('centralai.pay.none')}</Text>}
        </div>
      ))}
      {offered.length > 0 && (
        <Cluster gap={3} align="end" wrap>
          <CapWidth size="sm">
            <Field label={t('centralai.pay.add')} htmlFor="centralai-pay-add">
              <Select id="centralai-pay-add" value={adding} onChange={(e) => setAdding(e.target.value)}>
                <option value="">{t('centralai.pay.add.choose')}</option>
                {offered.map((o) => <option key={o.service} value={o.service}>{o.label}</option>)}
              </Select>
            </Field>
          </CapWidth>
          <Button variant="secondary" disabled={adding === ''} onClick={() => { emit(addOrderService(services, adding)); setAdding(''); }}>{t('centralai.pay.add.action')}</Button>
        </Cluster>
      )}
    </Stack>
  );
}

// ---- ai.models -----------------------------------------------------------------------------------

function AiModelsEditor({ value, bucket, onValue }: { value: unknown; bucket: GovernedSettingBucketView; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const [rows, setRows] = useState<readonly AiModelRow[]>(() => aiModelRowsOf(value));
  const emit = (purpose: AiPurpose, patch: Partial<Pick<AiModelRow, 'model' | 'fallbackModel'>>): void => {
    const next = withAiModelRow(rows, purpose, patch);
    setRows(next);
    const out = aiModelsValue(next);
    onValue(Object.keys(out.purposes).length === 0 ? undefined : out);
  };
  return (
    <Stack gap={4}>
      <Notice tone="neutral">{t('centralai.models.explain')}</Notice>
      {bucket.scope === 'country' && <Text as="p" role="caption" tone="tertiary">{t('centralai.models.inherit')}</Text>}
      {AI_PURPOSES.map((purpose) => {
        const row = rows.find((r) => r.purpose === purpose) as AiModelRow;
        return (
          <Grid key={purpose} min="sm" gap={3}>
            <Field label={t('centralai.models.model', { purpose: v('ai.purpose', purpose) })} htmlFor={`centralai-model-${purpose}`}>
              <TextInput id={`centralai-model-${purpose}`} autoComplete="off" spellCheck={false} placeholder={t('centralai.models.model.ph')} value={row.model} onChange={(e) => emit(purpose, { model: e.target.value })} />
            </Field>
            <Field label={t('centralai.models.fallback', { purpose: v('ai.purpose', purpose) })} htmlFor={`centralai-fallback-${purpose}`}>
              <TextInput id={`centralai-fallback-${purpose}`} autoComplete="off" spellCheck={false} placeholder={t('centralai.models.model.ph')} value={row.fallbackModel} onChange={(e) => emit(purpose, { fallbackModel: e.target.value })} />
            </Field>
          </Grid>
        );
      })}
    </Stack>
  );
}

// ---- ai.speechEngine -----------------------------------------------------------------------------

function SpeechEngineEditor({ value, onValue }: { value: unknown; onValue: (v: unknown) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const [draft, setDraft] = useState<SpeechEngineDraft>(() => speechEngineDraftOf(value));
  const emit = (next: SpeechEngineDraft): void => {
    setDraft(next);
    onValue(next.engine === 'self_hosted' && next.credentialRef.trim() === '' ? undefined : speechEngineValue(next));
  };
  const ref = draft.engine === 'self_hosted' ? speechCredentialRefOf(draft) : undefined;
  const [slot, setSlot] = useState<{ ref: string; configured: boolean; enabled: boolean } | undefined>(undefined);
  useEffect(() => {
    if (ref === undefined) return undefined;
    let alive = true;
    void (async () => {
      const res = await runtime.integrations.getAiCredentials([ref]);
      const found = res.ok ? res.value.speech.find((s) => s.ref === ref) : undefined;
      if (alive) setSlot(found === undefined ? undefined : { ref, configured: found.configured, enabled: found.enabled });
    })();
    return () => {
      alive = false;
    };
  }, [runtime, ref]);
  const status = ref !== undefined && slot?.ref === ref ? slot : undefined;
  return (
    <Stack gap={4}>
      <CapWidth size="md">
        <Field label={t('centralai.speech.engine')} htmlFor="centralai-engine" hint={t('centralai.speech.engine.hint')}>
          <Select id="centralai-engine" value={draft.engine} onChange={(e) => emit(e.target.value === 'self_hosted' ? { engine: 'self_hosted', credentialRef: '' } : { engine: 'openrouter' })}>
            <option value="openrouter">{v('ai.speech_engine', 'openrouter')}</option>
            <option value="self_hosted">{v('ai.speech_engine', 'self_hosted')}</option>
          </Select>
        </Field>
      </CapWidth>
      {draft.engine === 'self_hosted' && (
        <>
          <CapWidth size="sm">
            <Field label={t('centralai.speech.ref')} htmlFor="centralai-speech-ref" hint={t('centralai.speech.ref.hint')}>
              <TextInput id="centralai-speech-ref" autoComplete="off" spellCheck={false} maxLength={41} value={draft.credentialRef} onChange={(e) => emit({ engine: 'self_hosted', credentialRef: e.target.value.trim() })} />
            </Field>
          </CapWidth>
          {ref !== undefined && (
            <Notice tone={status?.configured === true && status.enabled ? 'success' : 'warning'}>
              {status?.configured === true ? (status.enabled ? t('centralai.speech.slot.ready') : t('centralai.speech.slot.off')) : t('centralai.speech.slot.missing')}
            </Notice>
          )}
        </>
      )}
    </Stack>
  );
}

// ---- ai.monthlyBudget ----------------------------------------------------------------------------

function AiBudgetEditor({ value, bucket, now, onValue }: { value: unknown; bucket: GovernedSettingBucketView; now: string; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const stored = (value as { currency?: unknown } | null | undefined)?.currency;
  const { currency: known, looking } = useBucketCurrency([stored, (bucket.current?.value as { currency?: unknown } | undefined)?.currency], bucket, now);
  const [typed, setTyped] = useState('');
  const currency = known ?? typed;
  const [draft, setDraft] = useState<AiBudgetDraft>(() => aiBudgetDraftOf(value, ''));
  const [percentsText, setPercentsText] = useState(() => aiBudgetDraftOf(value, '').alertPercents.join(', '));
  const emit = (next: AiBudgetDraft): void => {
    setDraft(next);
    onValue(aiBudgetValue({ ...next, currency }));
  };
  useEffect(() => {
    onValue(aiBudgetValue({ ...draft, currency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);
  if (looking) return <Notice tone="neutral">{t('govset.editor.money.loading')}</Notice>;
  return (
    <Stack gap={4}>
      <Notice tone="neutral">{t(bucket.scope === 'platform' ? 'centralai.budget.explain.platform' : 'centralai.budget.explain.country')}</Notice>
      {known === undefined && <CurrencyField value={typed} onChange={setTyped} />}
      {/^[A-Z]{3}$/.test(currency) ? (
        <Amount id="centralai-budget" label={t('centralai.budget.amount')} hint={t('centralai.budget.amount.hint', { currency })} currency={currency} valueMinor={draft.amountMinor} onChange={(m) => emit({ ...draft, amountMinor: m })} />
      ) : (
        <Notice tone="warning">{t('govset.editor.money.noCurrency')}</Notice>
      )}
      <CapWidth size="sm">
        <Field label={t('centralai.budget.alerts')} htmlFor="centralai-budget-alerts" hint={t('centralai.budget.alerts.hint')}>
          <TextInput
            id="centralai-budget-alerts"
            inputMode="numeric"
            autoComplete="off"
            value={percentsText}
            onChange={(e) => {
              setPercentsText(e.target.value);
              emit({ ...draft, alertPercents: parseAlertPercents(e.target.value) });
            }}
          />
        </Field>
      </CapWidth>
    </Stack>
  );
}

// ---- central.autonomy (D639) ---------------------------------------------------------------------

/**
 * How far Vello may act alone: the master switch, the level of every topic (shadow = Vello drafts and a person sends), and the kill switches by
 * channel and by topic. What Vello can do, and which topics the simulation lets be promoted, comes from the server (`/v1/velo/intents`): a topic
 * whose suite does not pass is offered shadow ONLY - the server refuses the rest, the form just does not offer it. While that list cannot be read
 * the form offers no promotion at all (never a guess).
 */
function AutonomyEditor({ value, onValue }: { value: unknown; onValue: (v: unknown) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const { runtime } = useAdmin();
  const [draft, setDraft] = useState<AutonomyDraft>(() => autonomyDraftOf(value));
  const [catalog, setCatalog] = useState<{ intents: readonly VeloIntentView[]; channels: readonly string[] } | 'loading' | 'unreadable'>('loading');
  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await runtime.velo.intents();
      if (!alive) return;
      setCatalog(res.ok ? { intents: res.value.intents, channels: res.value.channels } : 'unreadable');
    })();
    return () => {
      alive = false;
    };
  }, [runtime]);
  // The form is always complete: report the value in force (or the conservative default) as soon as it is shown.
  useEffect(() => {
    onValue(autonomyValue(draft));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const emit = (next: AutonomyDraft): void => {
    setDraft(next);
    onValue(autonomyValue(next));
  };
  if (catalog === 'loading') return <Notice tone="neutral">{t('centralai.autonomy.loading')}</Notice>;
  const intents = catalog === 'unreadable' ? [] : catalog.intents;
  const channels = catalog === 'unreadable' ? [] : catalog.channels;
  return (
    <Stack gap={4}>
      <Notice tone="neutral">{t('centralai.autonomy.explain')}</Notice>
      {catalog === 'unreadable' && <Notice tone="warning">{t('centralai.autonomy.unreadable')}</Notice>}
      <div>
        <Switch checked={draft.enabled} label={t('centralai.autonomy.enabled')} onChange={(on) => emit(autonomyWithEnabled(draft, on))} />
        <Text as="p" role="caption" tone="tertiary">{t('centralai.autonomy.enabled.hint')}</Text>
      </div>
      <CapWidth size="sm">
        <Field label={t('centralai.autonomy.default')} htmlFor="centralai-autonomy-default" hint={t('centralai.autonomy.default.hint')}>
          <Select id="centralai-autonomy-default" value={draft.defaultLevel} onChange={(e) => emit(autonomyWithDefaultLevel(draft, e.target.value as AutonomyLevel))}>
            {autonomyLevelChoices(intents.length > 0 && intents.every((i) => i.promotable)).map((l) => <option key={l} value={l}>{v('central.autonomy_level', l)}</option>)}
          </Select>
        </Field>
      </CapWidth>
      {intents.length > 0 && (
        <Stack gap={3}>
          <Text role="label">{t('centralai.autonomy.intents')}</Text>
          <Grid min="md" gap={3}>
            {intents.map((i) => (
              <Field key={i.id} label={v('velo.intent', i.id)} htmlFor={`centralai-autonomy-${i.id}`} hint={i.promotable ? undefined : t(i.awaitsSurface !== undefined ? 'centralai.autonomy.awaitsSurface' : 'centralai.autonomy.notPromotable')}>
                <Select
                  id={`centralai-autonomy-${i.id}`}
                  value={draft.intents[i.id] ?? ''}
                  onChange={(e) => emit(autonomyWithIntentLevel(draft, i.id, e.target.value === '' ? undefined : (e.target.value as AutonomyLevel)))}
                >
                  <option value="">{t('centralai.autonomy.follows')}</option>
                  {autonomyLevelChoices(i.promotable).map((l) => <option key={l} value={l}>{v('central.autonomy_level', l)}</option>)}
                </Select>
              </Field>
            ))}
          </Grid>
        </Stack>
      )}
      {channels.length > 0 && (
        <div>
          <Text role="label" block>{t('centralai.autonomy.channels')}</Text>
          <Text as="p" role="caption" tone="tertiary">{t('centralai.autonomy.channels.hint')}</Text>
          <Cluster gap={3} wrap>
            {channels.map((c) => (
              <Checkbox key={c} checked={draft.disabledChannels.includes(c)} label={v('conversation.channel', c)} onChange={() => emit(autonomyToggleDisabled(draft, 'disabledChannels', c))} />
            ))}
          </Cluster>
        </div>
      )}
      {intents.length > 0 && (
        <div>
          <Text role="label" block>{t('centralai.autonomy.killIntents')}</Text>
          <Text as="p" role="caption" tone="tertiary">{t('centralai.autonomy.killIntents.hint')}</Text>
          <Cluster gap={3} wrap>
            {intents.map((i) => (
              <Checkbox key={i.id} checked={draft.disabledIntents.includes(i.id)} label={v('velo.intent', i.id)} onChange={() => emit(autonomyToggleDisabled(draft, 'disabledIntents', i.id))} />
            ))}
          </Cluster>
        </div>
      )}
    </Stack>
  );
}
